import React, { useCallback, useEffect, useState } from 'react';
import { Button, Form, Input, Select, Tag, Tooltip, Space } from 'antd';
import { ClockCircleOutlined, PauseCircleOutlined, CheckOutlined, MailOutlined } from '@ant-design/icons';
import CBModal from '../../../components/CBModal';
import axiosInstance from '../../../variables/api/axiosInstance';
import { toast } from 'react-toastify';
import { libelleVictime } from '../../../variables/constants';
import { useTranslation } from 'react-i18next';
import { avecLibelleTraduit, libellesTraduits } from '../../../i18n/libelles';

const GREEN = '#1a6b3c';

/**
 * Parties dont le dossier peut attendre une action.
 *
 * INTERNE existe pour que le motif réel soit dit plutôt que masqué, mais il ne
 * dédouane pas l'agent : le temps d'attente interne reste compté dans son délai
 * de traitement. Le libellé le rappelle, pour qu'on ne s'en serve pas par
 * commodité.
 */
const PARTIES = avecLibelleTraduit([
  { value: 'VICTIME' },
  { value: 'BENEFICIAIRE' },
  { value: 'HOMOLOGUE' },
  { value: 'COMPAGNIE_MEMBRE' },
  { value: 'EXPERT' },
  { value: 'AUTORITE' },
  { value: 'INTERNE' },
], 'dossierSinistre:bandeauAttentes.parties');

const LIBELLE_PARTIE = libellesTraduits('dossierSinistre:bandeauAttentes.parties');

/**
 * Ce qui bloque le dossier, et qui doit agir.
 *
 * Répond à la situation que rien ne savait décrire : le chèque est prêt, la
 * victime est injoignable, et le dossier ne peut pas se clore. Sans cette
 * distinction, un tel dossier est indiscernable d'un dossier laissé traîner -
 * et c'est l'agent qui en porte le retard.
 *
 * Le bandeau ne s'affiche QUE s'il y a quelque chose à dire : un encart
 * permanent « aucune attente » sur tous les dossiers serait du bruit.
 */
export default function BandeauAttentes({ sinistreId, victimes = [], onChange }) {
  const { t } = useTranslation('dossierSinistre');
  const [attentes, setAttentes] = useState([]);
  const [ouvrirModal, setOuvrirModal] = useState(false);
  const [cloturerCible, setCloturerCible] = useState(null);
  const [enCours, setEnCours] = useState(false);
  const [formOuvrir] = Form.useForm();
  const [formCloturer] = Form.useForm();

  const charger = useCallback(() => {
    if (!sinistreId) return;
    axiosInstance.get(`/v1/attentes/sinistre/${sinistreId}`, { skipErrorToast: true })
      .then((r) => setAttentes(r.data?.data ?? []))
      .catch(() => setAttentes([]));
  }, [sinistreId]);

  useEffect(() => { charger(); }, [charger]);

  const ouvertes = attentes.filter((a) => a.ouverte);

  const soumettreOuverture = async (v) => {
    setEnCours(true);
    try {
      await axiosInstance.post(`/v1/attentes/sinistre/${sinistreId}`, {
        partieAttendue: v.partieAttendue,
        motif: v.motif,
        victimeTrackingId: v.victimeTrackingId || null,
      });
      toast.success(t('bandeauAttentes.enregistree'));
      formOuvrir.resetFields();
      setOuvrirModal(false);
      charger();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('bandeauAttentes.enregistrementImpossible'));
    } finally { setEnCours(false); }
  };

  const soumettreCloture = async (v) => {
    setEnCours(true);
    try {
      await axiosInstance.patch(`/v1/attentes/${cloturerCible.attenteTrackingId}/cloturer`,
        { resolution: v.resolution });
      toast.success(t('bandeauAttentes.levee'));
      formCloturer.resetFields();
      setCloturerCible(null);
      charger();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('bandeauAttentes.leveeImpossible'));
    } finally { setEnCours(false); }
  };

  return (
    <>
      {ouvertes.length > 0 && (
        <div style={{
          margin: '16px 0', padding: '14px 20px', borderRadius: 12,
          background: '#fffbeb', border: '1px solid #fde68a',
          display: 'flex', flexDirection: 'column', gap: 10,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <PauseCircleOutlined style={{ fontSize: 18, color: '#b45309' }} />
            <span style={{ fontWeight: 700, fontSize: 14, color: '#78350f' }}>
              {t('bandeauAttentes.titre')}
            </span>
            <span style={{ fontSize: 12, color: '#92400e' }}>
              {t('bandeauAttentes.nonImpute')}
            </span>
          </div>

          {ouvertes.map((a) => (
            <div key={a.attenteTrackingId} style={{
              display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap',
              padding: '10px 12px', borderRadius: 8, background: '#fff',
            }}>
              <div style={{ flex: 1, minWidth: 220 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <Tag color="warning" style={{ borderRadius: 14, fontWeight: 600, marginInlineEnd: 0 }}>
                    {LIBELLE_PARTIE[a.partieAttendue] || a.partieAttendue}
                  </Tag>
                  <span style={{ fontSize: 12, color: '#6b7280' }}>
                    <ClockCircleOutlined style={{ marginRight: 4 }} />
                    {t('bandeauAttentes.depuisJours', { count: a.joursEcoules })}
                  </span>
                  {a.nbTentatives > 0 && (
                    <Tooltip title={t('bandeauAttentes.relancesEnvoyees')}>
                      <span style={{ fontSize: 12, color: '#6b7280' }}>
                        <MailOutlined style={{ marginRight: 4 }} />
                        {t('bandeauAttentes.tentatives', { count: a.nbTentatives })}
                      </span>
                    </Tooltip>
                  )}
                  {a.victimeNomComplet && (
                    <span style={{ fontSize: 12, color: '#6b7280' }}>· {a.victimeNomComplet}</span>
                  )}
                </div>
                <div style={{ fontSize: 13, color: '#374151', marginTop: 4, whiteSpace: 'pre-wrap' }}>
                  {a.motif}
                </div>
              </div>
              <Button size="small" icon={<CheckOutlined />} onClick={() => setCloturerCible(a)}>
                {t('bandeauAttentes.tiersAAgi')}
              </Button>
            </div>
          ))}
        </div>
      )}

      <Button
        size="small"
        icon={<PauseCircleOutlined />}
        onClick={() => setOuvrirModal(true)}
        style={{ marginBottom: ouvertes.length > 0 ? 0 : 12 }}
      >
        {t('bandeauAttentes.signaler')}
      </Button>

      <CBModal
        open={ouvrirModal}
        onCancel={() => setOuvrirModal(false)}
        title={t('bandeauAttentes.signaler')}
        footer={null}
        width={560}
        destroyOnClose
      >
        <p style={{ fontSize: 12.5, color: '#6b7280', marginTop: 0 }}>
          {t('bandeauAttentes.statutInchange')}
        </p>
        <Form form={formOuvrir} layout="vertical" onFinish={soumettreOuverture}>
          <Form.Item name="partieAttendue" label={t('bandeauAttentes.quiDoitAgir')} rules={[{ required: true }]}>
            <Select placeholder={t('bandeauAttentes.selectionner')} options={PARTIES} />
          </Form.Item>
          {victimes.length > 0 && (
            <Form.Item
              name="victimeTrackingId"
              label={t('bandeauAttentes.victimeConcernee')}
              extra={t('bandeauAttentes.victimeAide')}
            >
              <Select
                allowClear
                placeholder={t('bandeauAttentes.toutLeDossier')}
                options={victimes.map((v) => ({
                  value: v.victimeTrackingId,
                  label: libelleVictime(v),
                }))}
              />
            </Form.Item>
          )}
          <Form.Item
            name="motif"
            label={t('bandeauAttentes.quAttendOn')}
            rules={[{ required: true, message: t('bandeauAttentes.motifObligatoire') }]}
          >
            <Input.TextArea rows={3} placeholder={t('bandeauAttentes.motifPlaceholder')} />
          </Form.Item>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, borderTop: '1px solid #e5e7eb', paddingTop: 16 }}>
            <Button onClick={() => setOuvrirModal(false)}>{t('bandeauAttentes.annuler')}</Button>
            <Button type="primary" htmlType="submit" loading={enCours}
              style={{ background: GREEN, borderColor: GREEN }}>
              {t('bandeauAttentes.enregistrer')}
            </Button>
          </div>
        </Form>
      </CBModal>

      <CBModal
        open={!!cloturerCible}
        onCancel={() => setCloturerCible(null)}
        title={t('bandeauAttentes.lever')}
        footer={null}
        width={520}
        destroyOnClose
      >
        <Space direction="vertical" size={4} style={{ marginBottom: 14 }}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>
            {LIBELLE_PARTIE[cloturerCible?.partieAttendue] || cloturerCible?.partieAttendue}
          </span>
          <span style={{ fontSize: 12.5, color: '#6b7280' }}>{cloturerCible?.motif}</span>
        </Space>
        <Form form={formCloturer} layout="vertical" onFinish={soumettreCloture}>
          <Form.Item
            name="resolution"
            label={t('bandeauAttentes.queSestIlPasse')}
            rules={[{ required: true, message: t('bandeauAttentes.resolutionObligatoire') }]}
          >
            <Input.TextArea rows={3} placeholder={t('bandeauAttentes.resolutionPlaceholder')} />
          </Form.Item>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, borderTop: '1px solid #e5e7eb', paddingTop: 16 }}>
            <Button onClick={() => setCloturerCible(null)}>{t('bandeauAttentes.annuler')}</Button>
            <Button type="primary" htmlType="submit" loading={enCours}
              style={{ background: GREEN, borderColor: GREEN }}>
              {t('bandeauAttentes.lever')}
            </Button>
          </div>
        </Form>
      </CBModal>
    </>
  );
}
